import { useState } from "react";
import { ButtonProps } from "@campgladiator/cgui-core.atoms.button";
import { CreateMembershipModalSteps } from "../CreateMembershipModal";
import {
  ProductDetailDTO,
  ProgramScheduleDTO,
  Role,
} from "@campgladiator/cg-common.types.types";
import useSelectCouponInput from "../../SelectCouponInput/useSelectCouponInput";
import { SelectedTrainer } from "../../SelectTrainerInput/SelectTrainerInput";
import { Option } from "@campgladiator/cgui-core.atoms.dropdown";
import { PaymentManagementProps } from "@campgladiator/cg-common.ui.payment-management";
import { CreateMembershipConfirmProps } from "../components/CreateMembershipConfirm";
import { centsToUSD } from "@campgladiator/cg-common.utility.numbers";
import { addDaysToStartDateReturnMMDDYYYY } from "@campgladiator/cg-common.utility.dates";
import { TermsAndConditionsProps } from "../../TermsAndConditions/TermsAndConditions";
import useTermsAndConditions from "../../TermsAndConditions/useTermsAndConditions";
import useCreateMembershipModalSelectProductStep from "./useCreateMembershipModalSelectProductStep";
import useCreateMembershipModalButtons from "./useCreateMembershipModalButtons";
import { getNewMembershipPrice } from "../logic/newMembershipPrice";

export type UseWithCreateMembershipModalProps = {
  onClose?: (success?: boolean) => void;
  theme: ButtonProps["theme"];
  isInitialLoading: boolean;
  productDetails: ProductDetailDTO[];
  productOptions: Option[];
  userId: string;
  stripeCustomerId: string;
  sellingTrainerId: string;
  role: Role;
};

const useWithCreateMembershipModal = ({
  onClose,
  theme,
  isInitialLoading,
  productDetails,
  productOptions,
  sellingTrainerId,
  stripeCustomerId,
  userId,
  role,
}: UseWithCreateMembershipModalProps) => {
  const [step, setStep] = useState<CreateMembershipModalSteps>("selectProduct");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedProductOption, setSelectedProductOption] =
    useState<Option | null>(null);
  const [selectedTrainer, setSelectedTrainer] = useState<
    SelectedTrainer | undefined
  >(undefined);
  const [isTrainerLoading, setIsTrainerLoading] = useState<boolean>(false);
  const [selectedTrainerSchedules, setSelectedTrainerSchedules] = useState<
    ProgramScheduleDTO[]
  >([]);
  const [selectedScheduleOption, setSelectedScheduleOption] =
    useState<Option>();
  const [trialDays, setTrialDays] = useState<number>(0);

  const { couponOptions, handleOnSelectCoupon, selectedCouponOption } =
    useSelectCouponInput({
      role,
      selectedProductId: selectedProductOption?.metadata?.id,
    });
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] =
    useState<string>();
  const [isTermChecked, setIsTermChecked] = useState<boolean>(false);

  const {
    selectedProductType,
    isScheduleRequired,
    createMembershipSelectProductProps,
  } = useCreateMembershipModalSelectProductStep({
    productDetails,
    selectedProductOption,
    setSelectedTrainer,
    setSelectedProductOption,
    selectedTrainer,
    selectedTrainerSchedules,
    setIsTrainerLoading,
    setSelectedScheduleOption,
    productOptions,
    setTrialDays,
    trialDays,
    couponOptions,
    handleOnSelectCoupon,
    setSelectedTrainerSchedules,
  });

  const hasTrainerGrants = ["TRAINER", "ADMIN", "HQ"].includes(role);
  const { termsUrl } = useTermsAndConditions({
    subscriptionProductType: selectedProductType,
    hasTrainerGrants,
  });
  const isLoading = isInitialLoading || isTrainerLoading || isSubmitting;
  const paymentStartDate = addDaysToStartDateReturnMMDDYYYY(
    new Date().toISOString(),
    trialDays
  );

  const { buttons, getTitleBasedOnStep } = useCreateMembershipModalButtons({
    step,
    isLoading,
    selectedProductOption,
    selectedTrainer,
    selectedScheduleOption,
    isScheduleRequired,
    trialDays,
    selectedPaymentMethodId,
    paymentStartDate,
    isTermChecked,
    setIsSubmitting,
    setStep,
    onClose,
    theme,
    userId,
    stripeCustomerId,
    sellingTrainerId,
    selectedCouponOption,
    setSelectedPaymentMethodId,
    setIsTermChecked,
  });

  const onChangeTermsCheckbox = (isChecked: boolean) => {
    setIsTermChecked(isChecked);
  };

  const paymentManagementProps: PaymentManagementProps = {
    stripeCustomerId,
    onError: (error: any) => {
      onClose(false);
    },
    onSelectPaymentMethod: (paymentMethodId: string) => {
      setSelectedPaymentMethodId(paymentMethodId);
    },
  };

  const { totalDueToday, selectedPriceAmount } = getNewMembershipPrice(
    selectedProductOption?.metadata?.prices[0].amount,
    trialDays,
    selectedCouponOption?.percentageDiscount,
    selectedCouponOption?.amountOff
  );

  const createMembershipConfirmProps: CreateMembershipConfirmProps = {
    couponAmountOff: selectedCouponOption?.amountOff?.toString(),
    couponName: selectedCouponOption?.couponCode,
    couponPercentageDiscount:
      selectedCouponOption?.percentageDiscount?.toString(),
    productName: selectedProductOption?.metadata?.stripeProductName,
    productPrice: centsToUSD(selectedPriceAmount),
    trialDays: trialDays,
    totalDueToday,
    paymentStartDate: paymentStartDate,
    trainerName: selectedTrainer?.text,
    trainerPhotoUrl: selectedTrainer?.avatar?.src,
    scheduleDescription: selectedScheduleOption?.text,
  };
  const termsAndConditionsProps: TermsAndConditionsProps = {
    hasTrainerGrants,
    termDocumentUrl: termsUrl,
    productName: selectedProductOption?.metadata?.stripeProductName,
    productPrice: centsToUSD(selectedPriceAmount),
    onChangeTermsCheckbox,
  };

  return {
    title: getTitleBasedOnStep(),
    buttons,
    step,
    createMembershipSelectProductProps,
    createMembershipConfirmProps,
    paymentManagementProps,
    termsAndConditionsProps,
    isScheduleRequired,
  };
};

export default useWithCreateMembershipModal;
